/**
 * E-Invoicing Configuration
 * Manages settings for invoice generation and delivery via Billit, UBL email, and future channels.
 */

/**
 * Supported e-invoicing delivery channels
 */
export enum EInvoicingChannel {
  BILLIT = 'BILLIT', // Send via Billit API (Peppol, email, post)
  EMAIL_UBL = 'EMAIL_UBL', // Send UBL XML as email attachment
  DISABLED = 'DISABLED', // E-invoicing disabled
}

/**
 * UBL format version
 */
export enum UBLVersion {
  PEPPOL_BIS_3 = 'PEPPOL_BIS_3', // Peppol BIS Billing 3.0 (based on UBL 2.1)
}

/**
 * Billit-specific configuration
 * Delivery is always via Peppol network
 */
export interface BillitConfig {
  apiKey?: string; // Billit API key (can be stored encrypted or in env)
  companyId?: string; // Billit company/dossier ID  
  defaultLanguage?: string; // Default invoice language (e.g., 'nl', 'fr', 'en')
  sendToPeppol?: boolean; // Send invoice via Peppol
}

/**
 * Email UBL configuration
 */
export interface EmailUBLConfig {
  ublVersion: UBLVersion;
  attachPdf?: boolean; // Also attach a PDF version
  toEmail?: string;
  ccEmails?: string[]; // CC recipients
}

/**
 * Seller information defaults (used when source doesn't provide seller info)
 */
export interface SellerDefaults {
  name: string;
  vatNumber?: string;
  companyId?: string;
  email?: string;
  phone?: string;
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  countryCode?: string;
  iban?: string;
  bic?: string;
  bankName?: string;
}

/**
 * Custom numbering configuration for e-invoicing.
 */
export interface CustomNumberingConfig {
  enabled: boolean;
  prefix: string; // supports {year}, {month}, {day} tokens
  creditNotePrefix: string;
}

/**
 * Main E-Invoicing Configuration Class
 * Only one channel can be active at a time.
 */
export class EInvoicingConfig {
  // Active channel (only one at a time)
  channel: EInvoicingChannel = EInvoicingChannel.DISABLED;

  // Manual processing mode: if true, invoices must be manually triggered
  // If false, invoices are automatically processed via the queue
  manualProcessing?: boolean = false;

  // Channel-specific configurations (used when channel matches)
  billit: BillitConfig = {
    defaultLanguage: 'fr',
    sendToPeppol: false,
  };

  emailUbl: EmailUBLConfig = {
    ublVersion: UBLVersion.PEPPOL_BIS_3,
    attachPdf: true,
  };

  // Custom numbering for e-invoicing
  customNumbering: CustomNumberingConfig = {
    enabled: false,
    prefix: 'EINV/{year}/',
    creditNotePrefix: 'ECNV/{year}/',
  };

  // Seller defaults (used when source doesn't provide)
  sellerDefaults: SellerDefaults = {
    name: '',
    countryCode: 'BE',
  };

  constructor(partial?: Partial<EInvoicingConfig>) {
    if (partial) {
      // Save nested defaults before Object.assign overwrites them
      const defaultBillit = { ...this.billit };
      const defaultEmailUbl = { ...this.emailUbl };
      const defaultCustomNumbering = { ...this.customNumbering };
      const defaultSellerDefaults = { ...this.sellerDefaults };

      Object.assign(this, partial);

      // Deep merge nested objects, restoring defaults if partial had undefined
      this.billit = { ...defaultBillit, ...(partial.billit || {}) };
      this.emailUbl = { ...defaultEmailUbl, ...(partial.emailUbl || {}) };
      this.customNumbering = { ...defaultCustomNumbering, ...(partial.customNumbering || {}) };
      this.sellerDefaults = { ...defaultSellerDefaults, ...(partial.sellerDefaults || {}) };
    }
  }

  /**
   * Check if e-invoicing is enabled
   */
  isEnabled(): boolean {
    return this.channel !== EInvoicingChannel.DISABLED;
  }
}
